import { useState, useCallback, useEffect, useRef } from "react";
import {
  sendToAssistant,
  fetchConversations,
  handleChatClick as apiHandleChatClick,
} from "../services/apiService";
import { createMessage } from "../services/utils";

export const useChat = (authToken, phoneNumber) => {
  const timeoutRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);

  //const deviceId = "Mozilla/5.0 (Linux; Android 13; 2201117TG Build/TKQ1.221114.001; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/140.0.7339.51 Mobile Safari/537.36";
  const deviceId = phoneNumber || navigator.userAgent || "TKQ1.221114.001";

  const [apiEndpoint, setApiEndpoint] = useState(
    localStorage.getItem("apiEndpoint") ||
      //"https://ai.wdi.net/api/Scraping/scraping_web"
      "https://ai.walleton.com/assistantwdi/api/Scraping/scraping_web"
      //"http://127.0.0.1:5000/api/Scraping/scraping_web"
    //localStorage.getItem('apiEndpoint') || 'https://ai.wdi.net/api/Scraping/scraping_question'
    //localStorage.getItem("apiEndpoint") || "http://127.0.0.1:5000/api/Scraping/scraping_web"
    //localStorage.getItem('apiEndpoint') || 'http://127.0.0.1:5000/api/Scraping/scraping_question'
  );

  const getDeviceConfig = useCallback(
    () => ({
      Lang: navigator.language || "es-PE",
      IdDevice: deviceId,
      SessionID: authToken || "",
      SessionRemID: !!authToken,
      Url: selectedChat || "",
    }),
    [authToken, phoneNumber, selectedChat, deviceId]
  );

  // 🧩 Restaurar el estado desde sessionStorage al montar
  useEffect(() => {
    const savedMessages = sessionStorage.getItem("chat_messages");
    const savedConversations = sessionStorage.getItem("chat_conversations");
    const savedSelectedChat = sessionStorage.getItem("chat_selected");

    if (savedMessages) setMessages(JSON.parse(savedMessages));
    if (savedConversations) setConversations(JSON.parse(savedConversations));
    if (savedSelectedChat) setSelectedChat(JSON.parse(savedSelectedChat));
  }, []);

  // 🧩 Guardar automáticamente cada vez que cambia algo importante
  useEffect(() => {
    sessionStorage.setItem("chat_messages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    sessionStorage.setItem("chat_conversations", JSON.stringify(conversations));
  }, [conversations]);

  useEffect(() => {
    sessionStorage.setItem("chat_selected", JSON.stringify(selectedChat));
  }, [selectedChat]);

  const handleSendMessage = useCallback(
    async (text, attachments) => {
      if ((!text && !attachments) || isLoading) return;

      clearTimeout(timeoutRef.current);

      const userMessage = createMessage(text, true, attachments);
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const deviceConfig = getDeviceConfig();
        const apiResponse = await sendToAssistant(apiEndpoint, {
          text,
          attachments,
          status: null,
          deviceConfig,
        });

        if (apiResponse.TextResponse) {
          const botMessage = createMessage(
            apiResponse.TextResponse,
            false,
            null,
            apiResponse
          );
          setMessages((prev) => [...prev, botMessage]);
        }

        if (apiResponse.Proccess === true) {
          const poll = async () => {
            try {
              const pollResponse = await sendToAssistant(apiEndpoint, {
                text: "",
                attachments: {},
                status: true,
                deviceConfig,
              });
              const botMessage = createMessage(
                pollResponse.TextResponse,
                false,
                null,
                pollResponse
              );
              setMessages((prev) => [...prev, botMessage]);

              if (pollResponse.Proccess === true) {
                // Guardamos la referencia al nuevo temporizador
                timeoutRef.current = setTimeout(poll, 15000);
              }
            } catch (pollError) {
              console.error("❌ Error en poll:", pollError);

              const userMessage =
                pollError?.message || "Error durante el procesamiento del asistente.";

              const errorMessage = createMessage(userMessage, false);
              setMessages((prev) => [...prev, errorMessage]);
            }
          };
          // Guardamos la referencia al nuevo temporizador
          timeoutRef.current = setTimeout(poll, 15000);
        }
      } catch (error) {
        const errorMessage = createMessage(
          "Error al comunicarse con el asistente.",
          false
        );
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
        if (!selectedChat) {
          // loadConversations();
        }
      }
    },
    [isLoading, getDeviceConfig, apiEndpoint, selectedChat]
  );

  const loadConversations = useCallback(async () => {
    try {
      const data = await fetchConversations(deviceId);
      if (data.success && data.data) {
        setConversations(data.data);
      }
    } catch (error) {
      console.error("No se pudieron cargar las conversaciones.");
    }
  }, [deviceId]);

  const handleChatClick = useCallback(
    async (url) => {
      setIsLoading(true);
      try {
        const data = await apiHandleChatClick(deviceId, url);
        if (data.success && data.record?.messages) {
          const formattedMessages = data.record.messages.map((msg) =>
            createMessage(msg.content, msg.role === "user", null, null)
          );
          setMessages(formattedMessages);
          setSelectedChat(url);
        } else {
          setMessages([]);
        }
      } catch (error) {
        setMessages([
          createMessage("Error al cargar el historial del chat.", false),
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [deviceId]
  );

  // 🧩 Cambiar esto — antes recargaba toda la página
  const handleInicioClick = useCallback((e) => {
    e.preventDefault();
    // Limpiar el chat sin recargar la página
    setSelectedChat(null);
    setMessages([]);
    sessionStorage.removeItem("chat_messages");
    sessionStorage.removeItem("chat_selected");
  }, []);

  const initializeChat = useCallback(async () => {
    if (messages.length > 0) return; // 🛑 Evita reinicio si ya hay mensajes
    setIsLoading(true);
    try {
      const deviceConfig = getDeviceConfig();
      const apiResponse = await sendToAssistant(apiEndpoint, {
        text: "{start}",
        attachments: {},
        deviceConfig,
      });
      const botMessage = createMessage(
        apiResponse.TextResponse,
        false,
        null,
        apiResponse
      );
      setMessages([botMessage]);
    } catch (error) {
      const errorMessage = createMessage(
        "No se pudo conectar con el asistente.",
        false
      );
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, getDeviceConfig, apiEndpoint]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedChat && authToken && phoneNumber) {
      initializeChat();
    } 
  }, [selectedChat, initializeChat, authToken, phoneNumber]);

  return {
    messages,
    isLoading,
    conversations,
    selectedChat,
    handleSendMessage,
    handleChatClick,
    handleInicioClick,
  };
};
